import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Activity, RefreshCw, Image as ImageIcon, ShieldCheck, Coins, Clock } from "lucide-react";

const JOB_LABELS = {
  market_refresh: "Gecelik Piyasa Taraması",
  image_selfheal: "Görsel Otomatik Onarım",
  weekly_summary: "Haftalık Özet",
  opportunity_email: "Fırsat E-postası",
  due_reminders: "Vade Hatırlatmaları",
  temp_price_lifecycle: "Geçici Fiyat Yaşam Döngüsü",
  public_day_lifecycle: "Halk Günü Yaşam Döngüsü",
  maintenance_window: "Bakım Penceresi",
  market_manual: "Elle Piyasa Fiyatı",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const StatusBadge = ({ running, finishedAt, stopReason }) => {
  if (running) return <span data-testid="job-status-running" className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 ring-1 ring-inset ring-blue-200">Çalışıyor</span>;
  if (stopReason === "cancelled") return <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">Durduruldu</span>;
  if (finishedAt) return <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">Tamamlandı</span>;
  return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500 ring-1 ring-inset ring-slate-200">Beklemede</span>;
};

const JobCard = ({ testid, icon: Icon, title, subtitle, running, done, total, finishedAt, stopReason, metrics }) => {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-2 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-green/10 text-brand-green"><Icon className="h-5 w-5" /></span>
          <div>
            <p className="font-heading text-sm font-bold text-slate-800">{title}</p>
            <p className="text-[11px] text-slate-400">{subtitle}</p>
          </div>
        </div>
        <StatusBadge running={running} finishedAt={finishedAt} stopReason={stopReason} />
      </div>
      {running && total > 0 && (
        <div className="mb-2">
          <div className="mb-1 flex justify-between text-[11px] text-slate-500"><span>{done}/{total}</span><span>%{pct}</span></div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-brand-green transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600">{metrics}</div>
      <p className="mt-2 flex items-center gap-1 text-[11px] text-slate-400"><Clock className="h-3 w-3" /> Son: {fmt(finishedAt || null)}</p>
    </div>
  );
};

export default function BackgroundJobsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    api.get("/jobs/overview").then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  if (loading && !data) return <div data-testid="jobs-panel-loading" className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-400">Yükleniyor…</div>;
  if (!data) return <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-400">Arka plan iş durumu alınamadı.</div>;

  const m = data.market || {}, ai = data.ai_image || {}, sh = data.selfheal || {};
  return (
    <div className="space-y-4" data-testid="background-jobs-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold text-slate-800">Arka Plan İşleri</h2>
          <p className="text-[12px] text-slate-500">Toplu piyasa fiyatı, AI görsel üretimi ve otomatik görsel onarımının canlı durumu ve geçmişi.</p>
        </div>
        <button data-testid="jobs-refresh-btn" onClick={load} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
          <RefreshCw className="h-3.5 w-3.5" /> Yenile
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <JobCard testid="job-market" icon={Coins} title="Toplu Piyasa Fiyatı" subtitle={m.method === "ai" ? "Google AI" : "Site Tarama"}
          running={m.running} done={m.done || 0} total={m.total || 0} finishedAt={m.finished_at} stopReason={m.stop_reason}
          metrics={<><span>✔ {m.ok || 0} bulundu</span><span>✕ {m.failed || 0} bulunamadı</span><span className="text-slate-400">Kapsam: {m.scope === "all" ? "Tümü" : "Fiyatsız"}</span></>} />
        <JobCard testid="job-ai-image" icon={ImageIcon} title="AI Görsel Üretimi" subtitle="Ürün görselleri"
          running={ai.running} done={ai.done || 0} total={ai.total || 0} finishedAt={ai.finished_at} stopReason={ai.stop_reason}
          metrics={<><span>✔ {ai.done || 0} üretildi</span><span>✕ {ai.failed || 0} atlandı</span></>} />
        <JobCard testid="job-selfheal" icon={ShieldCheck} title="Görsel Otomatik Onarım" subtitle="Haftalık self-heal"
          running={false} done={0} total={0} finishedAt={sh.finished_at} stopReason={null}
          metrics={<><span>{sh.fixed ? `${sh.fixed} onarıldı` : "Onarım gerekmedi"}</span></>} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="mb-2 flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><Activity className="h-4 w-4 text-brand-green" /> Son Çalışmalar</p>
        {(data.recent || []).length === 0 ? (
          <p className="text-[12px] text-slate-400">Henüz kayıtlı çalışma yok.</p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="jobs-recent-list">
            {(data.recent || []).map((r, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 text-[12px]">
                <span className="font-medium text-slate-700">{JOB_LABELS[r.job] || r.job}</span>
                <span className="flex items-center gap-3 text-slate-400">
                  {typeof r.fixed === "number" && r.fixed > 0 && <span className="text-emerald-600">{r.fixed} onarım</span>}
                  <span>{fmt(r.finished_at || r.started_at)}</span>
                  {r.finished_at ? <span className="text-emerald-500">•</span> : <span className="text-blue-500">•</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
